import React from 'react';
import { useNavigate } from 'react-router-dom';
import Wordmark from '../components/Wordmark';

/**
 * GoodbyePage — shown immediately after a user self-suspends their
 * account (DeleteAccountModal navigates here via window.location).
 *
 * The user is signed out at this point. The page just confirms the
 * deletion is scheduled, points at the recovery email, and gives them
 * an exit. Static content — no Firestore reads, no auth needed.
 *
 * The user's email is passed via ?email= query param so we can
 * show a concrete "recovery link sent to {email}" affirmation —
 * needed because the user is signed out and we have no other way
 * to surface their email at this point.
 */
export default function GoodbyePage() {
  const navigate = useNavigate();
  const email = (() => {
    try {
      return new URLSearchParams(window.location.search).get('email') || '';
    } catch { return ''; }
  })();
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', padding: '20px',
    }}>
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '40px 44px 36px',
        maxWidth: '460px',
        width: '100%',
        textAlign: 'center',
      }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
          <Wordmark fontSize={26} scaleWithUi={false} />
        </div>

        <div style={{
          fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600,
          color: 'var(--text)', marginBottom: 12,
        }}>
          your account is scheduled for deletion
        </div>

        {/* Prominent recovery-email affirmation — users testing this
            flow worried whether the email was actually sent. The
            green ✓ + concrete email address confirms it visually
            without making them dig through the paragraph below. */}
        <div style={{
          fontFamily: 'var(--mono)', fontSize: 13,
          color: 'var(--accent)', lineHeight: 1.5,
          marginBottom: 16,
          padding: '10px 14px',
          background: 'var(--surface2)',
          border: '1px solid var(--accent-dim)',
          borderRadius: 'var(--radius)',
        }}>
          ✓ recovery link sent
          {email && (
            <div style={{ marginTop: 4, color: 'var(--text-dim)', fontSize: 11 }}>
              to <span style={{ color: 'var(--text)' }}>{email}</span>
            </div>
          )}
        </div>

        <div style={{
          fontFamily: 'var(--mono)', fontSize: 13,
          color: 'var(--text-dim)', lineHeight: 1.6,
          marginBottom: 20,
        }}>
          you have <strong style={{ color: 'var(--text)' }}>7 days</strong> to change your mind —
          click the recovery link any time before then to restore everything.
          (check your spam folder if you don't see it.)
        </div>

        <div style={{
          fontFamily: 'var(--mono)', fontSize: 12,
          color: 'var(--text-dimmer)', lineHeight: 1.6,
          marginBottom: 24,
          padding: '10px 14px',
          background: 'var(--surface2)',
          border: '1px dashed var(--border)',
          borderRadius: 'var(--radius)',
        }}>
          after 7 days, your boards, lists, reminders, push subscriptions,
          and your access to anything shared with you will be permanently
          deleted. this can't be undone.
        </div>

        <button
          onClick={() => navigate('/', { replace: true })}
          style={{
            background: 'transparent',
            border: '1px solid var(--border-bright)',
            color: 'var(--text-dim)',
            fontFamily: 'var(--mono)',
            fontSize: 13,
            padding: '10px 16px',
            borderRadius: 'var(--radius)',
            cursor: 'pointer',
          }}
        >
          go to listicity
        </button>
      </div>
    </div>
  );
}
